import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { trackProgramEvent } from '../../lib/programsApi';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const shareReflectionWithImage = async (content, circle, anonymous, dedupeId, imageUrl) => {
  const res = await fetch(`${API_URL}/api/community/reflections`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('rbr_token')}` }, body: JSON.stringify({ content, circle, anonymous, dedupeId, imageUrl }) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || 'Could not share');
  return data;
};

const newId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export const ShareReflectionComposer = ({ open, onClose, text, circleId, circleName, prompt, programSlug, programDayId, day, allowAnonymous = true, imageUrl = null, heading = 'Share this reflection', openEvent = 'program_journal_share_opened', sharedEvent = 'program_journal_shared', attemptId }) => {
  const navigate = useNavigate();
  const [draft, setDraft] = useState(text || '');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [dedupeId, setDedupeId] = useState(newId);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(text || ''); setDone(null); setDedupeId(newId()); setTouched(false);
      if (programSlug) trackProgramEvent(programSlug, openEvent, { day, programDayId, circleId, attemptId });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (open && !touched && !done) setDraft(text || ''); }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;
  const dest = circleName || 'the Community';

  const post = async (anonymous) => {
    if (!draft.trim()) return toast.error('Write a few words first');
    setBusy(true);
    try {
      const r = await shareReflectionWithImage(draft.trim(), circleId || null, anonymous, dedupeId, imageUrl);
      if (programSlug) trackProgramEvent(programSlug, sharedEvent, { day, programDayId, circleId, shareMode: anonymous ? 'anonymous' : 'named', attemptId });
      setDone({ anonymous, id: r.reflection?.id });
      toast.success(`Shared ${anonymous ? 'anonymously ' : ''}with ${dest}`);
    } catch (e) { toast.error(e.message || 'Could not share right now'); } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[240] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose} data-testid="share-reflection-composer">
      <div className="w-full max-w-md bg-[#FFFCF3] rounded-t-3xl sm:rounded-3xl p-5 animate-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-1">
          <Users className="w-4 h-4 text-[#7a9b8e]" />
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#7a9b8e] flex-1">{heading}</p>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full hover:bg-black/5 flex items-center justify-center" aria-label="Close" data-testid="share-reflection-close"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-sm font-semibold text-[#0f1e33] mb-1">Share with: <span data-testid="share-reflection-circle">{dest}</span></p>
        {prompt && <p className="text-xs text-[#7a9b8e] italic mb-3" data-testid="share-reflection-prompt">“{prompt}”</p>}
        {done ? (
          <div className="rounded-2xl bg-[#f9f4ea] border border-[#7a9b8e]/30 p-3" data-testid="share-reflection-done">
            <p className="text-sm font-semibold text-[#5c8477]">✓ Shared {done.anonymous ? 'anonymously ' : ''}with {dest}</p>
            <p className="text-[11px] text-[#7a9b8e] mt-1">Your private journal entry is unchanged.</p>
            <div className="flex gap-2 mt-3">
              {circleId && <button type="button" onClick={() => navigate(`/circles/${circleId}`)} className="h-10 px-4 rounded-full bg-[#0f1e33] text-[#f9f4ea] text-xs font-semibold uppercase tracking-[0.15em]" data-testid="share-reflection-view-post">View Post</button>}
              <button type="button" onClick={onClose} className="h-10 px-4 rounded-full border border-[#0f1e33]/20 text-xs font-semibold uppercase tracking-[0.15em]" data-testid="share-reflection-finish">Done</button>
            </div>
          </div>
        ) : (
          <>
            {imageUrl && <img src={`${API_URL}${imageUrl}`} alt="Journey postcard" className="w-32 rounded-xl mb-2 border border-[#e3d2a8]/60" data-testid="share-reflection-image" />}
            <p className="text-[11px] text-[#7a9b8e] mb-2">{imageUrl ? 'Edit or replace the caption — only the postcard and your words are shared.' : "Edit freely — share only what you're comfortable with. Your journal stays private."}</p>
            <textarea value={draft} onChange={(e) => { setDraft(e.target.value); setTouched(true); }} rows={5} maxLength={500}
              className="w-full rounded-2xl border border-[#e3d2a8]/60 bg-white/80 p-3 text-sm text-[#0f1e33] focus:outline-none focus:border-[#D6AD60] resize-none" data-testid="share-reflection-textarea" />
            <p className="text-[10px] text-[#7a9b8e] text-right mb-2">{draft.length}/500</p>
            <div className={`grid gap-2 ${allowAnonymous ? 'grid-cols-2' : 'grid-cols-1'}`}>
              <button type="button" disabled={busy} onClick={() => post(false)} className="h-11 rounded-full bg-[#0f1e33] text-[#f9f4ea] text-xs font-semibold uppercase tracking-[0.15em] disabled:opacity-50" data-testid="share-reflection-post-named">Post with my name</button>
              {allowAnonymous && <button type="button" disabled={busy} onClick={() => post(true)} className="h-11 rounded-full bg-[#7a9b8e] text-white text-xs font-semibold uppercase tracking-[0.15em] disabled:opacity-50" data-testid="share-reflection-post-anon">Post anonymously</button>}
            </div>
            <button type="button" onClick={onClose} className="w-full text-xs text-[#7a9b8e] py-3" data-testid="share-reflection-cancel">Cancel</button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};
